import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { recipes, mealplan } from '../lib/api';
import RecipeThumbnail from '../components/RecipeThumbnail';

const CATEGORIES = [
  { id: 'produce', label: 'Produce', icon: '🥬' },
  { id: 'dairy', label: 'Dairy', icon: '🥛' },
  { id: 'meat', label: 'Meat', icon: '🥩' },
  { id: 'seafood', label: 'Seafood', icon: '🐟' },
  { id: 'bakery', label: 'Bakery', icon: '🍞' },
  { id: 'frozen', label: 'Frozen', icon: '🧊' },
  { id: 'pantry', label: 'Pantry', icon: '🥫' },
  { id: 'spices', label: 'Spices & Seasonings', icon: '🧂' },
  { id: 'other', label: 'Other', icon: '🛒' },
];

const CATEGORY_KEYWORDS = {
  produce: [
    'lettuce', 'spinach', 'kale', 'arugula', 'cabbage', 'broccoli', 'cauliflower',
    'carrot', 'celery', 'onion', 'garlic', 'ginger', 'potato', 'sweet potato',
    'tomato', 'cucumber', 'pepper', 'bell pepper', 'jalapeno', 'zucchini', 'squash',
    'eggplant', 'mushroom', 'corn', 'peas', 'green bean', 'asparagus', 'artichoke',
    'avocado', 'lemon', 'lime', 'orange', 'apple', 'banana', 'berry', 'strawberry',
    'blueberry', 'raspberry', 'grape', 'mango', 'pineapple', 'melon', 'watermelon',
    'peach', 'pear', 'plum', 'cherry', 'kiwi', 'papaya', 'coconut', 'cilantro',
    'parsley', 'basil', 'mint', 'dill', 'rosemary', 'thyme', 'sage', 'chive',
    'scallion', 'green onion', 'shallot', 'leek', 'radish', 'beet', 'turnip',
  ],
  dairy: [
    'milk', 'cream', 'half and half', 'butter', 'cheese', 'cheddar', 'mozzarella',
    'parmesan', 'feta', 'goat cheese', 'cream cheese', 'ricotta', 'cottage cheese',
    'yogurt', 'sour cream', 'whipping cream', 'heavy cream', 'egg', 'eggs',
  ],
  meat: [
    'beef', 'steak', 'ground beef', 'pork', 'bacon', 'ham', 'sausage', 'chicken',
    'turkey', 'lamb', 'veal', 'duck', 'goose', 'venison', 'bison', 'chorizo',
    'prosciutto', 'salami', 'pepperoni', 'hot dog', 'meatball', 'ribs', 'roast',
    'tenderloin', 'brisket', 'thigh', 'breast', 'wing', 'drumstick',
  ],
  seafood: [
    'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'halibut', 'trout', 'bass',
    'mackerel', 'sardine', 'anchovy', 'shrimp', 'prawn', 'crab', 'lobster',
    'scallop', 'mussel', 'clam', 'oyster', 'squid', 'calamari', 'octopus',
  ],
  bakery: [
    'bread', 'baguette', 'roll', 'bun', 'bagel', 'croissant', 'muffin', 'donut',
    'cake', 'pie', 'pastry', 'tortilla', 'pita', 'naan', 'flatbread',
  ],
  frozen: [
    'frozen', 'ice cream', 'sorbet', 'gelato', 'frozen pizza', 'frozen vegetable',
    'frozen fruit', 'frozen dinner', 'popsicle',
  ],
  pantry: [
    'flour', 'sugar', 'brown sugar', 'powdered sugar', 'honey', 'maple syrup',
    'molasses', 'rice', 'pasta', 'noodle', 'spaghetti', 'penne', 'macaroni',
    'quinoa', 'couscous', 'oat', 'cereal', 'granola', 'cracker', 'chip',
    'oil', 'olive oil', 'vegetable oil', 'coconut oil', 'sesame oil', 'vinegar',
    'balsamic', 'soy sauce', 'worcestershire', 'hot sauce', 'ketchup', 'mustard',
    'mayonnaise', 'tomato paste', 'tomato sauce', 'broth', 'stock', 'bouillon',
    'beans', 'black beans', 'kidney beans', 'chickpea', 'lentil', 'peanut butter',
    'almond butter', 'jam', 'jelly', 'preserves', 'canned', 'nut', 'almond',
    'walnut', 'pecan', 'cashew', 'peanut', 'dried fruit', 'raisin', 'cranberry',
    'chocolate', 'cocoa', 'vanilla', 'baking powder', 'baking soda', 'yeast',
    'cornstarch', 'breadcrumb', 'panko',
  ],
  spices: [
    'salt', 'pepper', 'black pepper', 'white pepper', 'cayenne', 'paprika',
    'smoked paprika', 'chili powder', 'cumin', 'coriander', 'turmeric', 'curry',
    'garam masala', 'cinnamon', 'nutmeg', 'clove', 'allspice', 'cardamom',
    'oregano', 'italian seasoning', 'bay leaf', 'fennel seed', 'mustard seed',
    'red pepper flakes', 'crushed red pepper', 'garlic powder', 'onion powder',
    'dried herb', 'dried basil', 'dried oregano', 'dried thyme', 'seasoning',
  ],
};

function categorizeIngredient(ingredient) {
  const lower = ingredient.toLowerCase();
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    for (const keyword of keywords) {
      if (lower.includes(keyword)) {
        return category;
      }
    }
  }
  return 'other';
}

function parseQuantity(text) {
  // Match patterns like "2 cups", "1/2 pound", "3.5 oz"
  const match = text.match(/^([\d.\/]+)\s*/);
  if (!match) return { quantity: null, unit: null, item: text.trim() };

  let quantity = match[1];
  if (quantity.includes('/')) {
    const [num, denom] = quantity.split('/');
    quantity = parseFloat(num) / parseFloat(denom);
  } else {
    quantity = parseFloat(quantity);
  }

  const rest = text.slice(match[0].length).trim();
  const unitMatch = rest.match(/^(cups?|tbsp?|tsp?|tablespoons?|teaspoons?|oz|ounces?|lbs?|pounds?|g|grams?|kg|ml|liters?|quarts?|pints?|gallons?|pieces?|slices?|cloves?|heads?|bunche?s?|cans?|jars?|packages?|bags?)\s*/i);

  if (unitMatch) {
    return {
      quantity,
      unit: unitMatch[1].toLowerCase(),
      item: rest.slice(unitMatch[0].length).trim(),
    };
  }

  return { quantity, unit: null, item: rest };
}

function normalizeUnit(unit) {
  if (!unit) return null;
  const unitMap = {
    'cup': 'cup', 'cups': 'cup',
    'tbsp': 'tbsp', 'tablespoon': 'tbsp', 'tablespoons': 'tbsp',
    'tsp': 'tsp', 'teaspoon': 'tsp', 'teaspoons': 'tsp',
    'oz': 'oz', 'ounce': 'oz', 'ounces': 'oz',
    'lb': 'lb', 'lbs': 'lb', 'pound': 'lb', 'pounds': 'lb',
    'g': 'g', 'gram': 'g', 'grams': 'g',
    'kg': 'kg',
    'ml': 'ml',
    'liter': 'L', 'liters': 'L',
    'quart': 'qt', 'quarts': 'qt',
    'pint': 'pt', 'pints': 'pt',
    'gallon': 'gal', 'gallons': 'gal',
    'piece': 'pc', 'pieces': 'pc',
    'slice': 'slice', 'slices': 'slice',
    'clove': 'clove', 'cloves': 'clove',
    'head': 'head', 'heads': 'head',
    'bunch': 'bunch', 'bunches': 'bunch',
    'can': 'can', 'cans': 'can',
    'jar': 'jar', 'jars': 'jar',
    'package': 'pkg', 'packages': 'pkg',
    'bag': 'bag', 'bags': 'bag',
  };
  return unitMap[unit] || unit;
}

function aggregateIngredients(selectedRecipes) {
  const aggregated = {};

  for (const recipe of selectedRecipes) {
    if (!recipe.ingredients) continue;

    let ingredients = [];
    if (typeof recipe.ingredients === 'string') {
      try {
        ingredients = JSON.parse(recipe.ingredients);
      } catch {
        ingredients = recipe.ingredients.split('\n').filter(Boolean);
      }
    } else if (Array.isArray(recipe.ingredients)) {
      ingredients = recipe.ingredients;
    }

    for (const ing of ingredients) {
      const text = typeof ing === 'string' ? ing : ing.text || ing.item || '';
      if (!text.trim()) continue;

      const { quantity, unit, item } = parseQuantity(text);
      const normalizedUnit = normalizeUnit(unit);
      const normalizedItem = item.toLowerCase().trim();
      const category = categorizeIngredient(normalizedItem);

      const key = `${normalizedItem}|${normalizedUnit || ''}`;

      if (aggregated[key]) {
        if (quantity && aggregated[key].quantity) {
          aggregated[key].quantity += quantity;
        }
        aggregated[key].recipes.push(recipe.title);
      } else {
        aggregated[key] = {
          item: normalizedItem,
          displayItem: item,
          quantity,
          unit: normalizedUnit,
          category,
          recipes: [recipe.title],
          checked: false,
        };
      }
    }
  }

  return Object.values(aggregated);
}

function ShoppingList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRecipeIds, setSelectedRecipeIds] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [showRecipePicker, setShowRecipePicker] = useState(true);
  const [mealPlanLoaded, setMealPlanLoaded] = useState(false);

  const fromMealPlan = searchParams.get('fromMealPlan') === 'true';
  const weekParam = searchParams.get('week');

  const { data: recipesData, isLoading } = useQuery({
    queryKey: ['recipes-for-shopping'],
    queryFn: () => recipes.list({ limit: 100 }),
  });

  // Fetch meal plan data if coming from meal plan
  const { data: mealPlanData } = useQuery({
    queryKey: ['mealplan-for-shopping', weekParam],
    queryFn: () => mealplan.getWeek(weekParam),
    enabled: fromMealPlan && !!weekParam,
  });

  // Auto-select recipes from meal plan
  useEffect(() => {
    if (fromMealPlan && mealPlanData?.mealPlans && !mealPlanLoaded) {
      const recipeIds = new Set();
      for (const dayMeals of Object.values(mealPlanData.mealPlans)) {
        for (const meal of Object.values(dayMeals)) {
          if (meal?.recipeId) {
            recipeIds.add(meal.recipeId);
          }
        }
      }
      if (recipeIds.size > 0) {
        setSelectedRecipeIds(Array.from(recipeIds));
        setShowRecipePicker(false);
        setMealPlanLoaded(true);
        // Clear URL params after loading
        setSearchParams({});
      }
    }
  }, [fromMealPlan, mealPlanData, mealPlanLoaded, setSearchParams]);

  const selectedRecipes = useMemo(() => {
    if (!recipesData?.recipes) return [];
    return recipesData.recipes.filter((r) => selectedRecipeIds.includes(r.id));
  }, [recipesData?.recipes, selectedRecipeIds]);

  const shoppingItems = useMemo(() => {
    return aggregateIngredients(selectedRecipes);
  }, [selectedRecipes]);

  const categorizedItems = useMemo(() => {
    const grouped = {};
    for (const item of shoppingItems) {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    }
    // Sort items within each category
    for (const category of Object.keys(grouped)) {
      grouped[category].sort((a, b) => a.displayItem.localeCompare(b.displayItem));
    }
    return grouped;
  }, [shoppingItems]);

  const toggleRecipe = (recipeId) => {
    setSelectedRecipeIds((prev) =>
      prev.includes(recipeId)
        ? prev.filter((id) => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  const toggleItem = (key) => {
    setCheckedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const uncheckAll = () => {
    setCheckedItems({});
  };

  const clearList = () => {
    setSelectedRecipeIds([]);
    setCheckedItems({});
  };

  const copyToClipboard = () => {
    const lines = [];
    for (const category of CATEGORIES) {
      const items = categorizedItems[category.id];
      if (!items?.length) continue;

      lines.push(`\n${category.icon} ${category.label}`);
      for (const item of items) {
        const checked = checkedItems[`${item.item}|${item.unit || ''}`];
        const prefix = checked ? '[x]' : '[ ]';
        const qty = item.quantity ? `${item.quantity} ${item.unit || ''}`.trim() : '';
        lines.push(`${prefix} ${qty} ${item.displayItem}`.trim());
      }
    }
    navigator.clipboard.writeText(lines.join('\n').trim());
  };

  const uncheckedCount = shoppingItems.filter(
    (item) => !checkedItems[`${item.item}|${item.unit || ''}`]
  ).length;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Shopping List</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {shoppingItems.length > 0
              ? `${uncheckedCount} items remaining`
              : 'Select recipes to generate your shopping list'}
          </p>
        </div>
        {shoppingItems.length > 0 && (
          <div className="flex gap-2">
            <button onClick={copyToClipboard} className="btn btn-secondary">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              Copy
            </button>
            <button onClick={uncheckAll} className="btn btn-secondary">
              Uncheck All
            </button>
            <button onClick={clearList} className="btn btn-secondary text-red-600 dark:text-red-400">
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recipe Selector */}
        <div className={`${showRecipePicker && shoppingItems.length > 0 ? '' : 'lg:col-span-1'}`}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <button
              onClick={() => setShowRecipePicker(!showRecipePicker)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">Select Recipes</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedRecipeIds.length} selected
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${showRecipePicker ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {showRecipePicker && (
              <div className="border-t border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : recipesData?.recipes?.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No recipes saved yet
                  </div>
                ) : (
                  <div className="p-2">
                    {recipesData?.recipes?.map((recipe) => (
                      <label
                        key={recipe.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRecipeIds.includes(recipe.id)}
                          onChange={() => toggleRecipe(recipe.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                          <RecipeThumbnail recipe={recipe} size="small" showTitle={false} />
                        </div>
                        <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white truncate">
                          {recipe.title}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Shopping List */}
        <div className={`${shoppingItems.length > 0 ? 'lg:col-span-2' : 'lg:col-span-2'}`}>
          {shoppingItems.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Your shopping list is empty
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Select recipes from the list to generate a shopping list with aggregated ingredients.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {CATEGORIES.map((category) => {
                const items = categorizedItems[category.id];
                if (!items?.length) return null;

                return (
                  <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <span>{category.icon}</span>
                        <span>{category.label}</span>
                        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                          ({items.length})
                        </span>
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {items.map((item) => {
                        const key = `${item.item}|${item.unit || ''}`;
                        const isChecked = checkedItems[key];

                        return (
                          <label
                            key={key}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                              isChecked ? 'bg-gray-50 dark:bg-gray-700/30' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked || false}
                              onChange={() => toggleItem(key)}
                              className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <div className="flex-1">
                              <p className={`font-medium ${isChecked ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                                {item.quantity && (
                                  <span className="text-primary-600 dark:text-primary-400">
                                    {Number.isInteger(item.quantity)
                                      ? item.quantity
                                      : item.quantity.toFixed(2).replace(/\.?0+$/, '')}{' '}
                                    {item.unit && `${item.unit} `}
                                  </span>
                                )}
                                {item.displayItem}
                              </p>
                              {item.recipes.length > 1 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  From: {item.recipes.join(', ')}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShoppingList;
